import { useEffect, useState } from 'react';
import Card from '../../components/Card.jsx';
import Chip from '../../components/Chip.jsx';
import Button from '../../components/Button.jsx';
import { researchApi } from '../../lib/apiClient.js';

export default function Research() {
  const [pubs, setPubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [qualifyStatus, setQualifyStatus] = useState(null);
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');
  const [publishStatus, setPublishStatus] = useState('idle'); // idle | sending | sent | error

  useEffect(() => {
    let alive = true;
    researchApi
      .list()
      .then((data) => alive && setPubs(Array.isArray(data) ? data : []))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  async function qualify() {
    setQualifyStatus('sending');
    try {
      await researchApi.qualify();
      setQualifyStatus('sent');
    } catch {
      setQualifyStatus('error');
    }
  }

  async function publish() {
    if (!title.trim() || !summary.trim()) return;
    setPublishStatus('sending');
    try {
      await researchApi.publish({
        title: title.trim(),
        summary: summary.trim(),
        body,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      });
      setTitle('');
      setSummary('');
      setBody('');
      setTags('');
      setPublishStatus('sent');
      const data = await researchApi.list();
      setPubs(Array.isArray(data) ? data : []);
    } catch {
      setPublishStatus('error');
    }
  }

  return (
    <>
      <Card>
        <h1 className="heading" style={{ fontSize: 22 }}>
          Research / Idea Marketplace
        </h1>
        <p className="aq-module-desc">
          Publication & discovery only. Transacting / copy-trading is Schedule B, Phase 2.
        </p>
      </Card>

      <section>
        <h2 className="aq-section-title heading">Latest publications</h2>
        {loading && <Card>Loading…</Card>}
        {!loading && pubs.length === 0 && (
          <Card>
            <div className="aq-empty-state">No publications yet.</div>
          </Card>
        )}
        <div className="aq-grid aq-grid--2">
          {pubs.map((p) => (
            <Card key={p.id} className="aq-rsr">
              <div className="aq-rsr-head">
                <h3 className="heading" style={{ fontSize: 16 }}>
                  {p.title}
                </h3>
                {p.verifiedTrackRecord && <Chip tone="gain">Verified track record</Chip>}
              </div>
              <p className="aq-module-desc">{p.summary}</p>
              {p.verifiedTrackRecord && p.authorStats && (
                <p className="aq-news-meta">
                  Verified author history (not this article): {p.authorStats.verifiedRuns} run
                  {p.authorStats.verifiedRuns === 1 ? '' : 's'}
                  {p.authorStats.bestReturn != null ? ` · best ${p.authorStats.bestReturn.toFixed(2)}%` : ''}
                  {p.authorStats.avgSharpe != null ? ` · avg Sharpe ${p.authorStats.avgSharpe.toFixed(2)}` : ''}
                </p>
              )}
              {!p.verifiedTrackRecord && <p className="aq-news-meta">Unverified publication · no qualifying positive track record</p>}
              <div className="aq-chip-row">
                {(p.tags || []).map((t) => (
                  <Chip key={t}>{t}</Chip>
                ))}
              </div>
              <span className="aq-news-meta">{new Date(p.publishedAt).toLocaleDateString()}</span>
            </Card>
          ))}
        </div>
      </section>

      <section>
        <h2 className="aq-section-title heading">Publish research</h2>
        <Card>
          <input
            type="text"
            placeholder="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <textarea
            placeholder="Summary"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            rows={2}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <textarea
            placeholder="Full write-up (optional)"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            style={{ width: '100%', marginBottom: 8 }}
          />
          <input
            type="text"
            placeholder="Tags, comma-separated (e.g. NGX, Momentum)"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            style={{ width: '100%', marginBottom: 12 }}
          />
          <Button onClick={publish} disabled={publishStatus === 'sending' || !title.trim() || !summary.trim()}>
            {publishStatus === 'sending' ? 'Publishing…' : 'Publish'}
          </Button>
          {publishStatus === 'sent' && <span className="aq-news-meta" style={{ marginLeft: 8 }}>Published ✓</span>}
          {publishStatus === 'error' && <span className="aq-news-meta" style={{ marginLeft: 8 }}>Publish failed — retry</span>}
        </Card>
      </section>

      <section>
        <Card className="aq-qual">
          <h3 className="heading" style={{ fontSize: 16 }}>
            Qualify as a strategy provider
          </h3>
<p className="aq-module-desc" style={{ marginBottom: 12 }}>
          Publish research with a verified track record backed by your AlphaQap backtest ledger.
          The strategy copier and revenue-share program are Schedule B — Phase 2; this only
          registers interest today. Ready to share a strategy? Publish it from your track record in the{' '}
          <a href="/app/strategies">Strategy Marketplace</a>.
        </p>
          <Button onClick={qualify} disabled={qualifyStatus === 'sending' || qualifyStatus === 'sent'}>
            {qualifyStatus === 'sent' ? 'Interest received' : qualifyStatus === 'sending' ? 'Sending…' : 'Register interest'}
          </Button>
        </Card>
      </section>
    </>
  );
}
